import React, { useState, useEffect } from 'react'
import { Modal, List, Typography, Input, Button, Spin, Alert } from 'antd'
import { Button as AntButton } from 'antd'
import Icon from 'components/Icon'
import { useCurrency } from 'contexts'
import styles from './CurrencySwitcher.scss'

const { Text } = Typography

interface CurrencySwitcherProps {
  currentCurrency?: string
  onCurrencyChange?: (currency: string) => void
}

const CurrencySwitcher: React.FC<CurrencySwitcherProps> = ({ currentCurrency = 'USD', onCurrencyChange }) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency)
  const [searchTerm, setSearchTerm] = useState('')

  const { currencies, isLoading, error, refreshCurrencies } = useCurrency()

  useEffect(() => {
    setSelectedCurrency(currentCurrency)
  }, [currentCurrency])

  const handleCurrencySelect = (currencyCode: string) => {
    setSelectedCurrency(currencyCode)
  }

  const showModal = () => {
    setSelectedCurrency(currentCurrency)
    setIsModalVisible(true)

    if (currencies.length === 0) {
      refreshCurrencies()
    }
  }

  const handleCancel = () => {
    setSelectedCurrency(currentCurrency)
    setIsModalVisible(false)
    setSearchTerm('')
  }

  const handleOk = () => {
    onCurrencyChange?.(selectedCurrency)
    setIsModalVisible(false)
    setSearchTerm('')
  }

  const filteredCurrencies = currencies.filter(
    currency =>
      currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.officialName.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const renderNoResults = () => (
    <div className={styles.noResultsContainer}>
      <Icon name='no-results' size='auto' className={styles.noResultsIcon} />
      <Text className={styles.noResultsMessage}>
        {currencies.length === 0
          ? 'No currencies available at the moment. Please try again later.'
          : 'Sorry, we do not support this currency at this moment'}
      </Text>
    </div>
  )

  const renderCurrencyList = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spin size='large' />
          <Text type='secondary'>Loading currencies...</Text>
        </div>
      )
    }

    if (error) {
      return (
        <Alert
          message='Error loading currencies'
          description={error}
          type='error'
          showIcon
          action={
            <Button size='small' onClick={refreshCurrencies}>
              Retry
            </Button>
          }
        />
      )
    }

    if (filteredCurrencies.length === 0) {
      return renderNoResults()
    }

    return (
      <List
        dataSource={filteredCurrencies}
        className={styles.currencyList}
        renderItem={currency => (
          <List.Item
            className={`${styles.currencyItem} ${selectedCurrency === currency.code ? styles.selected : ''}`}
            onClick={() => handleCurrencySelect(currency.code)}
          >
            <div className={styles.currencyInfo}>
              <Text strong className={styles.currencyCode}>
                {currency.code}
              </Text>
              <Text type='secondary' className={styles.currencyName}>
                {currency.officialName}
              </Text>
            </div>
            {selectedCurrency === currency.code && (
              <Icon name='check-circle-blue' size='regular' className={styles.checkIcon} />
            )}
          </List.Item>
        )}
      />
    )
  }

  return (
    <>
      <AntButton onClick={showModal} className={styles.currencyButton}>
        <Icon name='union' size='regular' />
        {currentCurrency}
      </AntButton>

      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        closable={false}
        footer={[
          <Button key='ok' type='primary' onClick={handleOk} className={styles.okButton}>
            OK
          </Button>,
        ]}
        width={610}
        className={styles.currencyModal}
      >
        <div className={styles.modalHeader}>
          <Typography.Title level={5} className={styles.modalTitle}>
            Available Currencies
          </Typography.Title>
          <Button
            type='text'
            icon={<Icon name='close-thin' size='regular' />}
            onClick={handleCancel}
            className={styles.closeButton}
          />
        </div>

        <div className={styles.modalContent}>
          <Input
            placeholder='Search for currency'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={styles.searchInput}
            suffix={<Icon name='search-md-grey' size='regular' />}
          />

          {renderCurrencyList()}
        </div>
      </Modal>
    </>
  )
}

export default CurrencySwitcher
